import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProgramDrafts } from '@/hooks/useProgramDrafts';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { Pencil, Trash2, Eye, CheckSquare, Square } from 'lucide-react';

export const DraftsList = () => {
  const navigate = useNavigate();
  const { drafts, isLoading, error, fetchDrafts, deleteDraft, bulkDeleteDrafts } = useProgramDrafts();
  const [selectedDrafts, setSelectedDrafts] = useState<string[]>([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const handleEdit = (draftId: string) => {
    navigate(`/programs/drafts/${draftId}/edit`);
  };

  const handlePreview = (draftId: string) => {
    navigate(`/programs/drafts/${draftId}/preview`);
  };

  const handleSelectDraft = (draftId: string, checked: boolean) => {
    if (checked) {
      setSelectedDrafts(prev => [...prev, draftId]);
    } else {
      setSelectedDrafts(prev => prev.filter(id => id !== draftId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDrafts(drafts.map(draft => draft._id));
    } else {
      setSelectedDrafts([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDrafts.length === 0) return;
    
    setIsBulkDeleting(true);
    try {
      await bulkDeleteDrafts(selectedDrafts);
      setSelectedDrafts([]);
      setIsSelectMode(false);
    } catch (error) {
      // Error is handled by the hook
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) {
      setSelectedDrafts([]);
    }
  };

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading drafts: {error}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="w-full">
            <CardHeader>
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
            <CardFooter>
              <Skeleton className="h-8 w-24" />
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No drafts found.</p>
        <Button
          className="mt-4"
          onClick={() => navigate('/programs/create')}
        >
          Create New Program
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Actions Header */}
      <div className="flex items-center justify-between bg-muted/50 p-4 rounded-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleSelectMode}
            disabled={isBulkDeleting}
            className={isSelectMode ? "bg-primary text-primary-foreground" : ""}
          >
            {isSelectMode ? <CheckSquare className="h-4 w-4 mr-2" /> : <Square className="h-4 w-4 mr-2" />}
            {isSelectMode ? "Exit Select Mode" : "Select Mode"}
          </Button>
          
          {isSelectMode && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedDrafts.length === drafts.length && drafts.length > 0}
                  onCheckedChange={handleSelectAll}
                  disabled={isBulkDeleting}
                />
                <span className="text-sm text-muted-foreground">
                  Select All ({selectedDrafts.length}/{drafts.length})
                </span>
              </div>
              
              {selectedDrafts.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      disabled={isBulkDeleting}
                    >
                      {isBulkDeleting ? (
                        <>
                          <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Selected ({selectedDrafts.length})
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Selected Drafts</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete {selectedDrafts.length} draft{selectedDrafts.length === 1 ? '' : 's'}? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleBulkDelete}
                        disabled={isBulkDeleting}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {isBulkDeleting ? (
                          <>
                            <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            Deleting...
                          </>
                        ) : (
                          `Delete ${selectedDrafts.length} Draft${selectedDrafts.length === 1 ? '' : 's'}`
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Drafts List */}
      <div className="space-y-4">
        {drafts.map((draft) => (
          <Card key={draft._id} className={isSelectMode ? "border-2" : ""}>
            <CardHeader>
              <div className="flex items-start gap-3">
                {isSelectMode && (
                  <Checkbox
                    checked={selectedDrafts.includes(draft._id)}
                    onCheckedChange={(checked) => handleSelectDraft(draft._id, checked as boolean)}
                    disabled={isBulkDeleting}
                    className="mt-1"
                  />
                )}
                <div className="flex-1">
                  <CardTitle>{draft.programData.title}</CardTitle>
                  <CardDescription>
                    Version {draft.version} • Last modified{' '}
                    {format(new Date(draft.lastModified), 'PPP')}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Note: 'about' is now per program type, not in main programData */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Type: {draft.programData.programType}</span>
                <span>•</span>
                <span>Language: {draft.programData.language}</span>
                <span>•</span>
                <span>Status: {draft.programData.status}</span>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePreview(draft._id)}
              >
                <Eye className="h-4 w-4 mr-1" />
                Preview
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit(draft._id)}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Draft</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this draft? This action cannot
                      be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteDraft(draft._id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}; 